import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { expValEquals } from '@confluence/feature-experiments';
import { isDefaultLiveDocTitle } from '@confluence/live-pages-utils/entry-points/isDefaultLiveDocTitle.ts';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { Provider } from '@atlaskit/collab-provider';
import type { CollabEditProvider } from '@atlaskit/editor-common/collab';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import {
	EDIT_PAGE_LOAD_EXPERIENCE,
	COMPANY_HUB_EDITOR_LOAD_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { getLogger } from '@confluence/logger';
import { COLLAB_EDIT_PROVIDER_MEASURES } from '@confluence/fabric-providers/entry-points/CollabEditProviderMeasures.ts';
import { CollabEditProviderMeasures } from '@confluence/action-measures/entry-points/CollabEditProviderMeasures.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import { useNativeCollabState } from './useNativeCollabState.ts';
import {
	useWaitForPublishButtonToBeEnabled,
	WaitForPublishButtonComponentUnmountedError,
	WaitForPublishButtonPreviousCallNotCompletedError,
	WaitForPublishButtonTabInactiveError,
} from './useWaitForPublishButtonToBeEnabled.ts';

const logger = getLogger('native-collab');

export const useInit = (
	collabEditProvider: (CollabEditProvider & Provider) | null,
	contentId: string,
	isCompanyHub?: boolean,
	isRovoLLMEnabled?: boolean,
) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [{ collabEditInitialized }, nativeCollabActions] = useNativeCollabState();

	const [title, setTitle] = useState<string | null>(null);
	// Track whether the doc was empty at init time (new live doc with no content).
	// Use a ref (not state) so the value is set synchronously in the init handler —
	// metadata:changed can fire immediately after init before React processes state updates.
	// Defaults to true so the first pre-init metadata:changed is suppressed for new docs.
	const isDocEmptyRef = useRef<boolean>(true);

	const experienceTracker = useContext(ExperienceTrackerContext);

	const isLivePage = useIsCurrentPageLive();

	let currentEditorPageLoadExperienceName = EDIT_PAGE_LOAD_EXPERIENCE;
	if (isLivePage) {
		currentEditorPageLoadExperienceName = VIEW_PAGE_EXPERIENCE;
	}
	if (isCompanyHub) {
		currentEditorPageLoadExperienceName = COMPANY_HUB_EDITOR_LOAD_EXPERIENCE;
	}
	const waitForPublishButtonToBeEnabled = useWaitForPublishButtonToBeEnabled();

	// Stores the publish button check until NCS has connected.
	// In the experiment path we prefer to start the check after `connected`, so the
	// publish button is no longer artificially disabled by `collabEditConnectionTimedOut`.
	// If `connected` already fired before `init`, we start the check immediately instead
	// of waiting for a callback that will never run.
	const onConnectedCallbackRef = useRef<(() => void) | null>(null);
	const hasConnectedRef = useRef<boolean>(false);

	const onConnected = useCallback(() => {
		hasConnectedRef.current = true;
		if (onConnectedCallbackRef.current) {
			onConnectedCallbackRef.current();
			onConnectedCallbackRef.current = null;
		}
	}, []);

	useEffect(() => {
		if (!collabEditProvider) {
			return;
		}

		const handleInit = (data) => {
			logger.debug`Collab Provider initialised at ${performance.now() / 1000}`;
			const newTitle = data?.metadata?.title ?? '';
			// For cwr_blank_object_experiment variant1 and variant2 on live docs,
			// do not auto-populate the title from the backend - leave it blank so
			// the user sees the "New live doc" placeholder text instead.
			// Only suppress when AI is enabled, the page is a live page, the doc
			// content is empty (BOE experience is shown), and the experiment is active.
			// Check if the ADF doc from the init payload is empty (matches the same
			// logic as isEmptyDocument in @confluence/rovo-editor-bridge-listener and
			// @atlassian/editor-rovo-bridge — a single empty paragraph/heading counts as empty).
			const doc = data?.doc;
			const docIsEmpty =
				!doc?.content ||
				doc.content.length === 0 ||
				(doc.content.length === 1 &&
					(doc.content[0].type === 'paragraph' || doc.content[0].type === 'heading') &&
					(!doc.content[0].content || doc.content[0].content.length === 0));
			isDocEmptyRef.current = docIsEmpty;
			const shouldSuppressTitle =
				isLivePage &&
				isRovoLLMEnabled &&
				docIsEmpty &&
				isDefaultLiveDocTitle(newTitle) &&
				(expValEquals('cwr_blank_object_experiment', 'cohort', 'variant1') ||
					expValEquals('cwr_blank_object_experiment', 'cohort', 'variant2'));
			setTitle(shouldSuppressTitle ? '' : newTitle);
			nativeCollabActions.setCollabEditInitialized(true);
			// marking provider initialization as complete once FE receives "init" event
			CollabEditProviderMeasures.markMeasureEnd(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_DISABLED,
			);
			// tracking time between when 'init' event is fired and connection has been established
			CollabEditProviderMeasures.markMeasureStart(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_INIT_DOC,
			);

			const commonAttributes = {
				collabService: 'ncs',
				isLivePage,
			};
			const onSuccess = () => {
				experienceTracker.succeed({
					name: currentEditorPageLoadExperienceName,
					attributes: {
						...commonAttributes,
					},
				});
			};
			const onFailure = (error: Error) => {
				if (
					error instanceof WaitForPublishButtonComponentUnmountedError ||
					error instanceof WaitForPublishButtonPreviousCallNotCompletedError ||
					error instanceof WaitForPublishButtonTabInactiveError
				) {
					experienceTracker.abort({
						name: currentEditorPageLoadExperienceName,
						reason: error.message,
						attributes: {
							...commonAttributes,
						},
						checkForTimeout: false,
					});
				} else {
					experienceTracker.fail({
						name: currentEditorPageLoadExperienceName,
						attributes: {
							...commonAttributes,
							failureReason: error.message,
						},
						error,
					});
				}
			};

			const isEmbedded = isEmbeddedConfluence_DO_NOT_USE();
			if (isLivePage || isEmbedded) {
				onSuccess();
			} else {
				if (
					!expValEquals('cc_delay_succeeding_edit_page_until_publish_button', 'isEnabled', true)
				) {
					// When experiment is disabled, succeed edit-page/load immediately.
					onSuccess();
				}
				const startCheckForPublishButton = (startedAfterConnected: boolean) => {
					waitForPublishButtonToBeEnabled().then(
						() => {
							createAnalyticsEvent({
								type: 'sendOperationalEvent',
								data: {
									action: 'enabled',
									actionSubject: 'editPagePublishButton',
									source: 'useInit',
									objectId: contentId,
									attributes: { startedAfterConnected },
								},
							}).fire();

							if (
								expValEquals(
									'cc_delay_succeeding_edit_page_until_publish_button',
									'isEnabled',
									true,
								)
							) {
								// When experiment is enabled, succeed edit-page/load after publish button appears.
								onSuccess();
							}
						},
						(error: Error) => {
							const wouldBeAnAbort =
								error instanceof WaitForPublishButtonComponentUnmountedError ||
								error instanceof WaitForPublishButtonPreviousCallNotCompletedError ||
								error instanceof WaitForPublishButtonTabInactiveError;
							createAnalyticsEvent({
								type: 'sendOperationalEvent',
								data: {
									action: 'failedToEnable',
									actionSubject: 'editPagePublishButton',
									source: 'useInit',
									objectId: contentId,
									attributes: {
										wouldBeAnAbort,
										errorMessage: error.message,
										startedAfterConnected,
									},
								},
							}).fire();

							if (
								expValEquals(
									'cc_delay_succeeding_edit_page_until_publish_button',
									'isEnabled',
									true,
								)
							) {
								// When experiment is enabled, fail edit-page/load after timeout.
								onFailure(error);
							}
						},
					);
				};

				if (expValEquals('cc_wait_for_publish_button_after_ncs_connected', 'isEnabled', true)) {
					if (hasConnectedRef.current) {
						// `connected` may fire before `init`. In that case, start the check immediately
						// instead of waiting for a second callback that will never arrive.
						startCheckForPublishButton(true);
						return;
					}
					// Defer the publish button check until after NCS fires `connected`.
					// By starting the check post-connection, `collabEditConnectionTimedOut`
					// is already cleared so the button is no longer artificially disabled
					// by a slow NCS handshake, eliminating false-positive timeout errors.
					// If NCS never fires `connected` (e.g. HOT-121344 scenario), the
					// `collabEditConnectionTimedOut` fallback in `onConnected` ensures
					// we still call the check after 10s, preserving monitoring.
					onConnectedCallbackRef.current = () => startCheckForPublishButton(true);
				} else {
					// Legacy behaviour: start the check immediately after `init`.
					startCheckForPublishButton(false);
				}
			}
		};
		collabEditProvider.on('init', handleInit);
		return () => {
			collabEditProvider.off('init', handleInit);

			const experienceState = experienceTracker.getExperienceState(
				currentEditorPageLoadExperienceName,
			);
			const experienceHasStopped = experienceState?.hasStopped ?? true;
			const experienceMatchesContentId = experienceState?.attributes?.contentId === contentId;

			// If we leave the editor page and the page we transition to immediately calls `useInit` (e.g. going from edit/live page to live page), this logic may undesirably try to abort the newly started experience. Using `experienceMatchesContentId` prevents this.
			if (!experienceHasStopped && experienceMatchesContentId) {
				experienceTracker.abort({
					name: currentEditorPageLoadExperienceName,
					reason: 'Unmounted before experience finished',
					attributes: {
						isLivePage,
					},
				});
			}
		};
	}, [
		waitForPublishButtonToBeEnabled,
		collabEditProvider,
		experienceTracker,
		nativeCollabActions,
		currentEditorPageLoadExperienceName,
		isLivePage,
		isRovoLLMEnabled,
		contentId,
		createAnalyticsEvent,
	]);

	return {
		collabEditInitialized,
		titleFromInit: title,
		isDocEmptyRef,
		onConnected,
	};
};
